import { toPng } from 'html-to-image';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertTriangle,
  Aperture,
  BringToFront,
  Camera,
  CheckCircle2,
  Clipboard,
  Download,
  ExternalLink,
  FileJson,
  FolderOpen,
  HeartHandshake,
  Keyboard,
  LocateFixed,
  MessageCircle,
  MousePointer2,
  PenLine,
  QrCode,
  Redo2,
  RotateCcw,
  Route,
  Settings2,
  Trash2,
  Undo2,
  Upload,
  X as CloseIcon
} from 'lucide-react';
import {
  buildShortcutHelpGroups,
  createDefaultShortcutMap,
  eventToShortcutCombo,
  findShortcutConflict,
  formatShortcutCombo,
  isEditableShortcutTarget,
  readShortcutMap,
  saveShortcutMap,
  shortcutActionForEvent,
  shortcutGroups,
  type ShortcutAction,
  type ShortcutHelpGroup,
  type ShortcutMap
} from './shortcuts';

type Tool = 'select' | 'character' | 'facing' | 'path' | 'camera';

type Point = {
  x: number;
  y: number;
};

type CharacterMark = Point & {
  id: string;
  name: string;
  color: string;
  labelSize?: number;
  labelWeight?: number;
  facing?: number;
  path: Point[];
  note?: string;
};

type CameraMark = Point & {
  id: string;
  label: string;
  angle: number;
  shotType: string;
  movement: string;
};

type ShotMapState = {
  background: string;
  backgroundName: string;
  shotTitle: string;
  sceneIntent: string;
  characters: CharacterMark[];
  cameras: CameraMark[];
};

type Selection =
  | { kind: 'character'; id: string }
  | { kind: 'camera'; id: string }
  | null;

type DragState = {
  kind: 'character' | 'camera';
  id: string;
  moved: boolean;
};

const STORAGE_KEY = 'shotmap-studio-mvp-state-v3';
const RECENT_CHARACTER_COLORS_STORAGE_KEY = 'shotmap-studio-recent-character-colors-v1';
const MAX_RECENT_CHARACTER_COLORS = 8;
const MIN_LABEL_SIZE = 14;
const MAX_LABEL_SIZE = 42;
const DEFAULT_LABEL_SIZE = 24;
const MIN_LABEL_WEIGHT = 400;
const MAX_LABEL_WEIGHT = 900;
const LABEL_WEIGHT_STEP = 100;
const DEFAULT_LABEL_WEIGHT = 700;
const BUILD_TAG = 'SHOTMAP_MVP_20260706_W';
const SAMPLE_SHOT_TITLE = '雨巷三人会合';
const SAMPLE_SHOT_INTENT = '店主守在雨棚下方，女主停在街心交涉，男主从左侧街口进入打断，主机位在右下方保留整条街的空间关系。';

const palette = [
  { name: '红', value: '#E84A3A' },
  { name: '蓝', value: '#2D9CDB' },
  { name: '绿', value: '#2EC4B6' },
  { name: '黄', value: '#DDAA33' },
  { name: '白', value: '#F3EFE6' },
  { name: '黑', value: '#111113' },
  { name: '橙', value: '#F28C38' },
  { name: '青', value: '#6FD3CF' }
];

const initialMap: ShotMapState = {
  background: '/sample-overhead-street.jpg',
  backgroundName: '原创俯瞰街道素材',
  shotTitle: SAMPLE_SHOT_TITLE,
  sceneIntent: SAMPLE_SHOT_INTENT,
  characters: [
    {
      id: 'char-lead',
      name: '店主',
      color: '#E84A3A',
      x: 31.5,
      y: 34.5,
      facing: 94,
      path: [],
      note: '站在雨棚下方的店门口，面向街心女主，不移动。'
    },
    {
      id: 'char-heroine',
      name: '女主',
      color: '#2D9CDB',
      x: 38.2,
      y: 55.2,
      facing: 252,
      path: [],
      note: '停在街道中央偏左位置，抬头面向店主，是对话焦点。'
    },
    {
      id: 'char-hero',
      name: '男主',
      color: '#2EC4B6',
      x: 7.8,
      y: 50.5,
      facing: 18,
      path: [
        { x: 15.4, y: 52.4 },
        { x: 24.8, y: 55.1 },
        { x: 33.4, y: 55.7 }
      ],
      note: '从左侧街口进入，沿虚线路径靠近女主，形成打断动作。'
    }
  ],
  cameras: [
    {
      id: 'cam-1',
      label: '主机位',
      x: 64,
      y: 81,
      angle: 248,
      shotType: '俯拍全景',
      movement: '固定',
    }
  ]
};

function readInitialState(): ShotMapState {
  try {
    const saved = window.localStorage.getItem(STORAGE_KEY);
    if (saved) {
      return JSON.parse(saved) as ShotMapState;
    }
  } catch {
    // Local storage may be unavailable in private contexts.
  }
  return initialMap;
}

function normalizeHexColor(value: string) {
  const compact = value.trim().replace(/^#/, '');
  const expanded =
    compact.length === 3 && /^[0-9a-f]{3}$/i.test(compact)
      ? compact
          .split('')
          .map((part) => part + part)
          .join('')
      : compact;
  return /^[0-9a-f]{6}$/i.test(expanded) ? `#${expanded.toUpperCase()}` : null;
}

function readRecentCharacterColors() {
  try {
    const saved = window.localStorage.getItem(RECENT_CHARACTER_COLORS_STORAGE_KEY);
    if (!saved) return [];
    const parsed = JSON.parse(saved);
    if (!Array.isArray(parsed)) return [];

    return Array.from(
      new Set(
        parsed
          .filter((value): value is string => typeof value === 'string')
          .map(normalizeHexColor)
          .filter((value): value is string => value !== null)
      )
    ).slice(0, MAX_RECENT_CHARACTER_COLORS);
  } catch {
    return [];
  }
}

function cloneState(state: ShotMapState): ShotMapState {
  return JSON.parse(JSON.stringify(state)) as ShotMapState;
}

function makeId(prefix: string) {
  return `${prefix}-${Date.now()}-${Math.random().toString(16).slice(2, 8)}`;
}

function clamp(value: number, min: number, max: number) {
  return Math.max(min, Math.min(max, value));
}

function normalizeNumberControl(value: number | undefined, min: number, max: number, step = 1, fallback = min) {
  const candidate = typeof value === 'number' && Number.isFinite(value) ? value : fallback;
  return Math.round(clamp(candidate, min, max) / step) * step;
}

function normalizeLabelSize(value?: number) {
  return normalizeNumberControl(value, MIN_LABEL_SIZE, MAX_LABEL_SIZE, 1, DEFAULT_LABEL_SIZE);
}

function normalizeLabelWeight(value?: number) {
  return normalizeNumberControl(value, MIN_LABEL_WEIGHT, MAX_LABEL_WEIGHT, LABEL_WEIGHT_STEP, DEFAULT_LABEL_WEIGHT);
}

function angleBetween(a: Point, b: Point) {
  return (Math.atan2(b.y - a.y, b.x - a.x) * 180) / Math.PI;
}

function pointFromEvent(event: React.PointerEvent | React.MouseEvent, element: HTMLElement): Point {
  const rect = element.getBoundingClientRect();
  return {
    x: clamp(((event.clientX - rect.left) / rect.width) * 100, 0, 100),
    y: clamp(((event.clientY - rect.top) / rect.height) * 100, 0, 100)
  };
}

function describeDirection(angle?: number) {
  if (angle === undefined) return '未设置朝向';
  const normalized = ((angle % 360) + 360) % 360;
  if (normalized >= 337.5 || normalized < 22.5) return '向右';
  if (normalized < 67.5) return '右下';
  if (normalized < 112.5) return '向下';
  if (normalized < 157.5) return '左下';
  if (normalized < 202.5) return '向左';
  if (normalized < 247.5) return '左上';
  if (normalized < 292.5) return '向上';
  return '右上';
}

function normalizeAngle(angle: number) {
  return ((angle % 360) + 360) % 360;
}

type LabelPlacement = 'top' | 'bottom' | 'left' | 'right';

function labelPlacementForAngle(angle?: number): LabelPlacement {
  if (angle === undefined) return 'top';
  const normalized = normalizeAngle(angle);
  if (normalized >= 315 || normalized < 45) return 'left';
  if (normalized < 135) return 'top';
  if (normalized < 225) return 'right';
  return 'bottom';
}

function sideFromVector(vector: Point): LabelPlacement {
  if (Math.abs(vector.x) >= Math.abs(vector.y)) return vector.x < 0 ? 'left' : 'right';
  return vector.y < 0 ? 'top' : 'bottom';
}

function fallbackPlacement(placement: LabelPlacement, angle?: number): LabelPlacement {
  if (placement === 'left' || placement === 'right') {
    const normalized = angle === undefined ? 90 : normalizeAngle(angle);
    return normalized >= 180 ? 'bottom' : 'top';
  }
  return placement === 'top' ? 'right' : 'left';
}

function labelPlacementForCharacter(character: CharacterMark, map: ShotMapState): LabelPlacement {
  let preferred = labelPlacementForAngle(character.facing);
  if (preferred === 'left' && character.x < 13) preferred = fallbackPlacement(preferred, character.facing);
  if (preferred === 'right' && character.x > 87) preferred = fallbackPlacement(preferred, character.facing);
  if (preferred === 'top' && character.y < 12) preferred = fallbackPlacement(preferred, character.facing);
  if (preferred === 'bottom' && character.y > 88) preferred = fallbackPlacement(preferred, character.facing);

  const hasIncomingPathConflict = map.characters.some((source) => {
    if (source.id === character.id || !source.path.length) return false;
    const terminal = source.path[source.path.length - 1];
    const vector = { x: terminal.x - character.x, y: terminal.y - character.y };
    const distance = Math.hypot(vector.x, vector.y);
    return distance < 9 && sideFromVector(vector) === preferred;
  });
  return hasIncomingPathConflict ? fallbackPlacement(preferred, character.facing) : preferred;
}

function vectorPoint(origin: Point, angle: number, distance: number): Point {
  const radians = (angle * Math.PI) / 180;
  return {
    x: origin.x + Math.cos(radians) * distance,
    y: origin.y + Math.sin(radians) * distance
  };
}

function arrowTipPath(from: Point, to: Point, length = 1.9, width = 0.95) {
  const angle = Math.atan2(to.y - from.y, to.x - from.x);
  const back = {
    x: to.x - Math.cos(angle) * length,
    y: to.y - Math.sin(angle) * length
  };
  const wing = {
    x: Math.cos(angle + Math.PI / 2) * width,
    y: Math.sin(angle + Math.PI / 2) * width
  };
  return `M ${back.x + wing.x} ${back.y + wing.y} L ${to.x} ${to.y} L ${back.x - wing.x} ${back.y - wing.y}`;
}

function buildPrompt(map: ShotMapState) {
  const characterLines = map.characters.map((character) => {
    const pathText =
      character.path.length > 0
        ? `移动路径经过 ${character.path.length} 个节点，终点约在画面 ${character.path.at(-1)?.x.toFixed(1)}% / ${character.path.at(-1)?.y.toFixed(1)}%。`
        : '保持当前位置。';
    const noteText = character.note ? `意图：${character.note}` : '';
    return `- ${character.name}: ${character.color} 标记，位于画面 ${character.x.toFixed(1)}% / ${character.y.toFixed(1)}%，${describeDirection(character.facing)}。${pathText}${noteText}`;
  });

  const cameraLines = map.cameras.map((camera) => {
    return `- ${camera.label}: ${camera.shotType}，${camera.movement}，位于画面 ${camera.x.toFixed(1)}% / ${camera.y.toFixed(1)}%，朝向 ${Math.round(camera.angle)} 度。`;
  });

  return [
    'Scene spatial blocking plan:',
    `Scene intent: ${map.sceneIntent || 'Use the marker positions, facing arrows, paths, and camera marks as the scene blocking source of truth.'}`,
    `Background reference: ${map.backgroundName}.`,
    'Characters:',
    characterLines.length ? characterLines.join('\n') : '- No characters placed yet.',
    'Camera:',
    cameraLines.length ? cameraLines.join('\n') : '- No camera marked yet.',
    'Generation constraint:',
    'Preserve relative positions, facing directions, and movement paths. Treat the map as the source of truth for character blocking before generating the video.',
    `Build tag: ${BUILD_TAG}`
  ].join('\n');
}

async function loadImage(src: string): Promise<HTMLImageElement> {
  const image = new Image();
  image.crossOrigin = 'anonymous';
  image.src = src;
  await image.decode();
  return image;
}

function drawArrowHead(
  context: CanvasRenderingContext2D,
  fromX: number,
  fromY: number,
  toX: number,
  toY: number,
  color: string,
  size = 18
) {
  const angle = Math.atan2(toY - fromY, toX - fromX);
  context.save();
  context.translate(toX, toY);
  context.rotate(angle);
  context.fillStyle = color;
  context.beginPath();
  context.moveTo(0, 0);
  context.lineTo(-size, size * 0.52);
  context.lineTo(-size, -size * 0.52);
  context.closePath();
  context.fill();
  context.restore();
}

function roundedRectPath(context: CanvasRenderingContext2D, x: number, y: number, width: number, height: number, radius: number) {
  const corner = Math.min(radius, width / 2, height / 2);
  context.beginPath();
  context.moveTo(x + corner, y);
  context.lineTo(x + width - corner, y);
  context.quadraticCurveTo(x + width, y, x + width, y + corner);
  context.lineTo(x + width, y + height - corner);
  context.quadraticCurveTo(x + width, y + height, x + width - corner, y + height);
  context.lineTo(x + corner, y + height);
  context.quadraticCurveTo(x, y + height, x, y + height - corner);
  context.lineTo(x, y + corner);
  context.quadraticCurveTo(x, y, x + corner, y);
  context.closePath();
}

function drawFloatingExportLegend(context: CanvasRenderingContext2D, width: number, height: number, map: ShotMapState) {
  const scale = width / 1664;
  const margin = Math.max(22, width * 0.022);
  const panelWidth = Math.min(width - margin * 2, width < 980 ? width * 0.82 : Math.min(420, Math.max(390, width * 0.28)));
  const padding = Math.max(16, Math.min(24, width * 0.014));
  const titleSize = Math.max(19, Math.min(25, width * 0.016));
  const labelSize = Math.max(14, Math.min(18, width * 0.0115));
  const detailSize = Math.max(12, Math.min(15, width * 0.009));
  const iconScale = Math.max(0.78, Math.min(1.06, scale));
  const items: { kind: 'dot' | 'facing' | 'dashed' | 'camera'; label: string; detail: string }[] = [];
  if (map.characters.length > 0) items.push({ kind: 'dot', label: '角色圆点', detail: '当前位置' });
  if (map.characters.some((character) => character.facing !== undefined)) {
    items.push({ kind: 'facing', label: '朝向尖角', detail: '面朝方向' });
  }
  if (map.characters.some((character) => character.path.length > 0)) {
    items.push({ kind: 'dashed', label: '虚线箭头', detail: '移动轨迹和方向' });
  }
  if (map.cameras.length > 0) items.push({ kind: 'camera', label: '琥珀机位', detail: '镜头位置和拍摄方向' });
  if (!items.length) return;

  const columns = width < 980 ? 1 : Math.min(2, items.length);
  const rows = Math.max(1, Math.ceil(items.length / columns));
  const rowHeight = Math.max(46, Math.min(58, width * 0.034));
  const headerHeight = titleSize + detailSize + Math.max(18, padding * 0.82);
  const panelHeight = padding * 2 + headerHeight + rows * rowHeight;
  const panelX = width - margin - panelWidth;
  const panelY = height - margin - panelHeight;
  const cellWidth = (panelWidth - padding * 2) / columns;
  const itemStartY = panelY + padding + headerHeight;

  context.save();
  context.shadowColor = 'rgba(0, 0, 0, 0.48)';
  context.shadowBlur = Math.max(18, width * 0.018);
  context.shadowOffsetY = Math.max(8, width * 0.006);
  roundedRectPath(context, panelX, panelY, panelWidth, panelHeight, Math.max(14, width * 0.009));
  context.fillStyle = 'rgba(17, 17, 19, 0.9)';
  context.fill();
  context.shadowColor = 'transparent';
  context.strokeStyle = 'rgba(221, 170, 51, 0.64)';
  context.lineWidth = Math.max(2, width * 0.0012);
  roundedRectPath(context, panelX, panelY, panelWidth, panelHeight, Math.max(14, width * 0.009));
  context.stroke();
  context.beginPath();
  context.moveTo(panelX + padding, panelY + padding + titleSize + 7);
  context.lineTo(panelX + panelWidth - padding, panelY + padding + titleSize + 7);
  context.strokeStyle = 'rgba(243, 239, 230, 0.12)';
  context.lineWidth = Math.max(1, width * 0.0008);
  context.stroke();

  context.textAlign = 'left';
  context.textBaseline = 'alphabetic';
  context.font = `800 ${titleSize}px "Noto Sans SC"`;
  context.fillStyle = '#DDAA33';
  context.fillText('图例说明 / LEGEND', panelX + padding, panelY + padding + titleSize);
  context.font = `600 ${detailSize}px "Noto Sans SC"`;
  context.fillStyle = 'rgba(243, 239, 230, 0.78)';
  context.fillText('仅说明当前画面已有标记', panelX + padding, panelY + padding + titleSize + detailSize + 22);

  items.forEach((item, index) => {
    const column = index % columns;
    const row = Math.floor(index / columns);
    const cellX = panelX + padding + column * cellWidth;
    const cellY = itemStartY + row * rowHeight;
    const iconX = cellX + Math.max(16, 20 * iconScale);
    const iconY = cellY + rowHeight * 0.52;
    const textX = cellX + Math.max(42, 48 * iconScale);
    const textMaxWidth = Math.max(80, cellWidth - (textX - cellX) - 8);

    context.save();
    if (item.kind === 'dot') {
      context.beginPath();
      context.arc(iconX, iconY, 10 * iconScale, 0, Math.PI * 2);
      context.fillStyle = '#E84A3A';
      context.fill();
      context.lineWidth = 3.5 * iconScale;
      context.strokeStyle = '#F3EFE6';
      context.stroke();
    }
    if (item.kind === 'facing') {
      drawArrowHead(context, iconX - 10 * iconScale, iconY, iconX + 10 * iconScale, iconY, '#2EC4B6', 11 * iconScale);
    }
    if (item.kind === 'dashed') {
      context.strokeStyle = '#2EC4B6';
      context.lineWidth = 3.5 * iconScale;
      context.lineCap = 'butt';
      context.setLineDash([8 * iconScale, 6 * iconScale]);
      context.beginPath();
      context.moveTo(iconX - 17 * iconScale, iconY);
      context.lineTo(iconX + 17 * iconScale, iconY);
      context.stroke();
      context.setLineDash([]);
      drawArrowHead(context, iconX - 17 * iconScale, iconY, iconX + 24 * iconScale, iconY, '#2EC4B6', 10 * iconScale);
    }
    if (item.kind === 'camera') {
      context.fillStyle = 'rgba(17, 17, 19, 0.9)';
      context.strokeStyle = '#DDAA33';
      context.lineWidth = 3 * iconScale;
      context.beginPath();
      context.arc(iconX - 5 * iconScale, iconY, 9.5 * iconScale, 0, Math.PI * 2);
      context.fill();
      context.stroke();
      drawArrowHead(context, iconX + 1 * iconScale, iconY + 3 * iconScale, iconX + 15 * iconScale, iconY - 7 * iconScale, '#DDAA33', 8.5 * iconScale);
    }
    context.restore();

    context.fillStyle = '#F3EFE6';
    context.font = `800 ${labelSize}px "Noto Sans SC"`;
    context.fillText(item.label, textX, cellY + rowHeight * 0.42, textMaxWidth);
    context.fillStyle = 'rgba(243, 239, 230, 0.72)';
    context.font = `600 ${detailSize}px "Noto Sans SC"`;
    context.fillText(item.detail, textX, cellY + rowHeight * 0.76, textMaxWidth);
  });
  context.restore();
}

async function exportPng(map: ShotMapState, stageElement: HTMLElement | null) {
  if (!stageElement) return;
  stageElement.classList.add('exporting');
  let stageDataUrl = '';
  try {
    await new Promise<void>((resolve) => {
      window.requestAnimationFrame(() => resolve());
    });
    stageDataUrl = await toPng(stageElement, {
      backgroundColor: '#0b0b0d',
      cacheBust: true,
      pixelRatio: Math.max(2, Math.min(3, window.devicePixelRatio || 2))
    });
  } finally {
    stageElement.classList.remove('exporting');
  }

  const image = await loadImage(stageDataUrl);
  const canvas = document.createElement('canvas');
  const imageWidth = image.naturalWidth || stageElement.offsetWidth || 1664;
  const imageHeight = image.naturalHeight || stageElement.offsetHeight || 1080;
  canvas.width = imageWidth;
  canvas.height = imageHeight;
  const context = canvas.getContext('2d');
  if (!context) return;

  context.drawImage(image, 0, 0, imageWidth, imageHeight);
  drawFloatingExportLegend(context, canvas.width, canvas.height, map);

  const link = document.createElement('a');
  link.download = `shotmap-${Date.now()}.png`;
  link.href = canvas.toDataURL('image/png');
  link.click();
}

function SupportPage() {
  useEffect(() => {
    document.title = '支持作者 - ShotMap Studio';
  }, []);

  return (
    <main className="support-shell">
      <header className="support-topbar">
        <a className="support-brand" href="/">
          <span className="brand-kicker">AI video blocking tool</span>
          <strong>ShotMap Studio</strong>
        </a>
        <nav className="support-nav" aria-label="支持页导航">
          <a className="toolbar-button" href="/">
            <MousePointer2 size={18} />
            <span>返回工具</span>
          </a>
          <a className="toolbar-button active" href="/support" aria-current="page">
            <HeartHandshake size={18} />
            <span>支持作者</span>
          </a>
        </nav>
      </header>

      <section className="support-page">
        <div className="support-hero">
          <div className="support-copy">
            <span className="brand-kicker">Support the maker</span>
            <h1>支持 ShotMap Studio 继续打磨</h1>
            <p>
              如果这个站位图工具帮你节省了分镜沟通、AI 视频提示词整理或镜头调度的时间，欢迎随喜支持我继续把它做得更稳定、更好用。
            </p>
          </div>
          <div className="support-note">
            <HeartHandshake size={22} />
            <p>支持完全自愿，不构成付费解锁或商业承诺。你也可以继续免费使用当前工具。</p>
          </div>
        </div>

        <section className="support-methods" aria-label="支持方式">
          <article className="support-method-card">
            <div className="support-method-head">
              <QrCode size={18} />
              <div>
                <strong>支付宝支持</strong>
                <span>打开支付宝扫码，自定义金额</span>
              </div>
            </div>
            <div className="support-qr-frame">
              <img src="/support/alipay-qr.jpg" alt="支付宝收款码" loading="eager" decoding="async" />
            </div>
            <p>建议直接扫码识别完整图片，不要裁切二维码。</p>
          </article>

          <article className="support-method-card">
            <div className="support-method-head">
              <QrCode size={18} />
              <div>
                <strong>微信支持</strong>
                <span>打开微信扫码，自定义金额</span>
              </div>
            </div>
            <div className="support-qr-frame">
              <img src="/support/wechat-qr.jpg" alt="微信收款码" loading="lazy" decoding="async" />
            </div>
            <p>手机端可以长按识别完整图片，也可以截图后扫码。</p>
          </article>

          <article className="support-method-card support-platform-card">
            <div className="support-method-head">
              <HeartHandshake size={18} />
              <div>
                <strong>通过爱发电支持</strong>
                <span>适合希望通过平台记录支持的朋友</span>
              </div>
            </div>
            <p>
              如果你想留下昵称、留言，或希望支持记录更清晰，可以通过爱发电完成。支付备注里也欢迎写下你希望优先改进的功能。
            </p>
            <a className="support-primary-link" href="https://www.ifdian.net/a/AsterXing" target="_blank" rel="noopener noreferrer">
              前往爱发电支持
              <ExternalLink size={16} />
            </a>
          </article>
        </section>

        <section className="support-contact" aria-label="联系作者">
          <div className="support-contact-copy">
            <MessageCircle size={22} />
            <span className="brand-kicker">Contact</span>
            <h2>联系作者</h2>
            <p>
              如果你想反馈工具问题、交流 AI 视频工作流、提出功能需求或聊合作，可以扫码添加我的微信。添加时可以简单备注来自 ShotMap Studio。
            </p>
          </div>
          <div className="support-contact-qr">
            <img src="/support/wechat-contact.jpg" alt="作者微信二维码" loading="lazy" decoding="async" />
          </div>
        </section>
      </section>
    </main>
  );
}

function ShortcutKey({ children }: { children: string }) {
  return <kbd className="shortcut-key">{children}</kbd>;
}

type ShortcutHelpPanelProps = {
  groups: ShortcutHelpGroup[];
  onOpenSettings: () => void;
};

function ShortcutHelpPanel({ groups, onOpenSettings }: ShortcutHelpPanelProps) {
  return (
    <div className="shortcut-help">
      <div className="panel-title shortcut-help-title">
        <span>
          <Keyboard size={16} />
          <span>快捷键</span>
        </span>
        <button className="mini-icon-button" onClick={onOpenSettings} title="快捷键设置" aria-label="快捷键设置">
          <Settings2 size={15} />
        </button>
      </div>
      <div className="shortcut-help-list">
        {groups.map((group) => (
          <div className="shortcut-help-group" key={group.title}>
            <span className="section-label">{group.title}</span>
            {group.items.map((item) => (
              <div className="shortcut-help-row" key={`${group.title}-${item.label}`}>
                <span className="shortcut-help-label">
                  {item.label}
                  {item.detail && <small>{item.detail}</small>}
                </span>
                <span className="shortcut-keyset">
                  {item.keys.map((key) => (
                    <ShortcutKey key={key}>{key}</ShortcutKey>
                  ))}
                </span>
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

type ShortcutSettingsDialogProps = {
  shortcuts: ShortcutMap;
  recordingAction: ShortcutAction | null;
  conflictMessage: string;
  onClose: () => void;
  onStartRecording: (action: ShortcutAction) => void;
  onResetAction: (action: ShortcutAction) => void;
  onResetAll: () => void;
};

function ShortcutSettingsDialog({
  shortcuts,
  recordingAction,
  conflictMessage,
  onClose,
  onStartRecording,
  onResetAction,
  onResetAll
}: ShortcutSettingsDialogProps) {
  return (
    <div className="shortcut-modal-backdrop" role="presentation">
      <section className="shortcut-modal" role="dialog" aria-modal="true" aria-labelledby="shortcut-settings-title">
        <header className="shortcut-modal-head">
          <div>
            <span className="brand-kicker">Keyboard Control</span>
            <h2 id="shortcut-settings-title">快捷键设置</h2>
          </div>
          <button className="icon-button" onClick={onClose} title="关闭快捷键设置" aria-label="关闭快捷键设置">
            <CloseIcon size={18} />
          </button>
        </header>

        {conflictMessage && (
          <div className="shortcut-conflict" role="alert">
            <AlertTriangle size={16} />
            <span>{conflictMessage}</span>
          </div>
        )}

        <div className="shortcut-settings-list">
          {shortcutGroups.map((group) => (
            <div className="shortcut-settings-group" key={group.title}>
              <span className="section-label">{group.title}</span>
              {group.definitions.map((definition) => {
                const combos = shortcuts[definition.action] ?? [];
                const isRecording = recordingAction === definition.action;
                return (
                  <div className={isRecording ? 'shortcut-setting-row recording' : 'shortcut-setting-row'} key={definition.action}>
                    <div className="shortcut-setting-copy">
                      <strong>{definition.label}</strong>
                      {definition.detail && <span>{definition.detail}</span>}
                    </div>
                    <div className="shortcut-setting-keys">
                      {isRecording ? (
                        <span className="recording-pill">按下新的快捷键</span>
                      ) : (
                        combos.map((combo) => <ShortcutKey key={combo}>{formatShortcutCombo(combo)}</ShortcutKey>)
                      )}
                    </div>
                    <div className="shortcut-setting-actions">
                      <button className="ghost-action" onClick={() => onStartRecording(definition.action)}>
                        {isRecording ? '录入中' : '录入'}
                      </button>
                      <button className="mini-icon-button" onClick={() => onResetAction(definition.action)} title="恢复此项默认值" aria-label={`恢复${definition.label}默认快捷键`}>
                        <RotateCcw size={14} />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          ))}
        </div>

        <footer className="shortcut-modal-foot">
          <p>录入时按 Esc 取消；若新快捷键已被其他动作占用，会提示冲突并保留原设置。</p>
          <button className="ghost-action" onClick={onResetAll}>
            <RotateCcw size={15} />
            恢复全部默认
          </button>
        </footer>
      </section>
    </div>
  );
}

type CharacterColorPickerProps = {
  value: string;
  recentColors: string[];
  onChange: (color: string) => void;
  onCustomColor: (color: string) => void;
};

function CharacterColorPicker({ value, recentColors, onChange, onCustomColor }: CharacterColorPickerProps) {
  const resolvedColor = normalizeHexColor(value) ?? palette[0].value;
  const [hexDraft, setHexDraft] = useState(resolvedColor);

  useEffect(() => {
    setHexDraft(resolvedColor);
  }, [resolvedColor]);

  const applyCustomColor = (candidate: string) => {
    const normalized = normalizeHexColor(candidate);
    if (!normalized) return false;
    onChange(normalized);
    onCustomColor(normalized);
    setHexDraft(normalized);
    return true;
  };

  return (
    <div className="character-color-picker">
      <div className="color-picker-section">
        <span className="color-picker-section-label">预设色</span>
        <div className="swatches">
          {palette.map((item) => (
            <button
              key={item.value}
              type="button"
              className={resolvedColor === item.value ? 'swatch selected' : 'swatch'}
              style={{ '--swatch-color': item.value } as React.CSSProperties}
              onClick={() => onChange(item.value)}
              aria-label={`选择${item.name}色`}
              title={item.name}
            >
              <span className="swatch-chip" aria-hidden="true" />
              <span className="swatch-name">{item.name}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="color-picker-section">
        <span className="color-picker-section-label">自定义色</span>
        <div className="color-picker-custom-row">
          <label className="native-color-field" title="打开系统取色器">
            <input
              type="color"
              value={resolvedColor}
              onChange={(event) => applyCustomColor(event.target.value)}
              aria-label="选择自定义角色颜色"
            />
            <span>取色</span>
          </label>
          <label className="hex-color-field">
            <span>HEX</span>
            <input
              value={hexDraft}
              onChange={(event) => {
                const next = event.target.value.toUpperCase();
                setHexDraft(next);
                applyCustomColor(next);
              }}
              onBlur={() => setHexDraft(resolvedColor)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') event.currentTarget.blur();
              }}
              inputMode="text"
              maxLength={7}
              spellCheck={false}
              aria-label="输入十六进制角色颜色"
            />
          </label>
        </div>
      </div>

      {recentColors.length > 0 && (
        <div className="color-picker-section recent-color-section">
          <span className="color-picker-section-label">最近使用</span>
          <div className="recent-color-swatches">
            {recentColors.map((color) => (
              <button
                key={color}
                type="button"
                className={resolvedColor === color ? 'recent-color-swatch selected' : 'recent-color-swatch'}
                style={{ '--swatch-color': color } as React.CSSProperties}
                onClick={() => onChange(color)}
                aria-label={`使用最近颜色 ${color}`}
                title={color}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

type RangeNumberControlProps = {
  label: string;
  description: string;
  hint: string;
  min: number;
  max: number;
  step: number;
  value: number;
  unit?: string;
  onChange: (value: number) => void;
};

function RangeNumberControl({ label, description, hint, min, max, step, value, unit, onChange }: RangeNumberControlProps) {
  const resolvedValue = normalizeNumberControl(value, min, max, step, min);
  const [draftValue, setDraftValue] = useState(String(resolvedValue));

  useEffect(() => {
    setDraftValue(String(resolvedValue));
  }, [resolvedValue]);

  const commitDraftValue = () => {
    const candidate = Number(draftValue);
    const nextValue = Number.isFinite(candidate) ? normalizeNumberControl(candidate, min, max, step, resolvedValue) : resolvedValue;
    setDraftValue(String(nextValue));
    if (nextValue !== resolvedValue) onChange(nextValue);
  };

  return (
    <div className="range-number-control">
      <div className="range-number-copy">
        <span>{label}</span>
        <small>{hint}</small>
      </div>
      <p className="range-number-description">{description}</p>
      <div className="range-number-inputs">
        <input
          type="range"
          min={min}
          max={max}
          step={step}
          value={resolvedValue}
          onChange={(event) => onChange(normalizeNumberControl(Number(event.target.value), min, max, step, resolvedValue))}
          aria-label={`调整${label}`}
        />
        <label className="range-number-field">
          <input
            type="number"
            min={min}
            max={max}
            step={step}
            value={draftValue}
            onChange={(event) => setDraftValue(event.target.value)}
            onBlur={commitDraftValue}
            onKeyDown={(event) => {
              if (event.key === 'Enter') event.currentTarget.blur();
            }}
            inputMode="numeric"
            aria-label={`输入${label}`}
          />
          {unit && <span>{unit}</span>}
        </label>
      </div>
    </div>
  );
}

type CharacterLabelTypographyControlsProps = {
  size: number;
  weight: number;
  onSizeChange: (value: number) => void;
  onWeightChange: (value: number) => void;
};

function CharacterLabelTypographyControls({ size, weight, onSizeChange, onWeightChange }: CharacterLabelTypographyControlsProps) {
  return (
    <div className="character-label-typography">
      <RangeNumberControl
        label="名称大小"
        description="数值越大，名称越显眼。"
        hint={`小 - 大 · ${MIN_LABEL_SIZE}-${MAX_LABEL_SIZE} px`}
        min={MIN_LABEL_SIZE}
        max={MAX_LABEL_SIZE}
        step={1}
        value={size}
        unit="px"
        onChange={onSizeChange}
      />
      <RangeNumberControl
        label="名称粗细"
        description="数值越大，笔画越粗。"
        hint={`细 - 粗 · ${MIN_LABEL_WEIGHT}-${MAX_LABEL_WEIGHT}`}
        min={MIN_LABEL_WEIGHT}
        max={MAX_LABEL_WEIGHT}
        step={LABEL_WEIGHT_STEP}
        value={weight}
        onChange={onWeightChange}
      />
    </div>
  );
}

function App() {
  const [map, setMap] = useState<ShotMapState>(() => readInitialState());
  const [tool, setTool] = useState<Tool>('select');
  const [selection, setSelection] = useState<Selection>(null);
  const [draftName, setDraftName] = useState('角色');
  const [draftColor, setDraftColor] = useState(palette[0].value);
  const [draftLabelSize, setDraftLabelSize] = useState(DEFAULT_LABEL_SIZE);
  const [draftLabelWeight, setDraftLabelWeight] = useState(DEFAULT_LABEL_WEIGHT);
  const [recentCharacterColors, setRecentCharacterColors] = useState<string[]>(() => readRecentCharacterColors());
  const [focusDraftName, setFocusDraftName] = useState(false);
  const [shortcuts, setShortcuts] = useState<ShortcutMap>(() => readShortcutMap());
  const [shortcutSettingsOpen, setShortcutSettingsOpen] = useState(false);
  const [recordingAction, setRecordingAction] = useState<ShortcutAction | null>(null);
  const [shortcutConflict, setShortcutConflict] = useState('');
  const [status, setStatus] = useState('当前示例表达：店主在门口，女主在街心，男主从左侧进入，右下机位俯拍全景。');
  const [toast, setToast] = useState<{ id: number; message: string } | null>(null);
  const [past, setPast] = useState<ShotMapState[]>([]);
  const [future, setFuture] = useState<ShotMapState[]>([]);
  const [dragging, setDragging] = useState<DragState | null>(null);
  const stageRef = useRef<HTMLDivElement | null>(null);
  const fileRef = useRef<HTMLInputElement | null>(null);
  const dragSnapshotRef = useRef<ShotMapState | null>(null);
  const toastIdRef = useRef(0);
  const toastTimerRef = useRef<number | null>(null);

  const selectedCharacter = useMemo(() => {
    if (selection?.kind !== 'character') return null;
    return map.characters.find((character) => character.id === selection.id) ?? null;
  }, [map.characters, selection]);

  const selectedCamera = useMemo(() => {
    if (selection?.kind !== 'camera') return null;
    return map.cameras.find((camera) => camera.id === selection.id) ?? null;
  }, [map.cameras, selection]);

  const prompt = useMemo(() => buildPrompt(map), [map]);
  const visibleShortcutGroups = useMemo(() => buildShortcutHelpGroups(shortcuts), [shortcuts]);

  const showStatus = (message: string) => {
    setStatus(message);
    const id = toastIdRef.current + 1;
    toastIdRef.current = id;
    if (toastTimerRef.current) {
      window.clearTimeout(toastTimerRef.current);
    }
    setToast({ id, message });
    toastTimerRef.current = window.setTimeout(() => {
      setToast((current) => (current?.id === id ? null : current));
    }, 3600);
  };

  useEffect(() => {
    return () => {
      if (toastTimerRef.current) window.clearTimeout(toastTimerRef.current);
    };
  }, []);

  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(map));
    } catch {
      showStatus('浏览器本地存储已满，当前编辑仍可导出。');
    }
  }, [map]);

  useEffect(() => {
    try {
      window.localStorage.setItem(RECENT_CHARACTER_COLORS_STORAGE_KEY, JSON.stringify(recentCharacterColors));
    } catch {
      showStatus('最近使用颜色暂时无法保存，当前页面仍可继续使用。');
    }
  }, [recentCharacterColors]);

  useEffect(() => {
    try {
      saveShortcutMap(shortcuts);
    } catch {
      showStatus('快捷键设置保存失败，当前页面内仍可继续使用。');
    }
  }, [shortcuts]);

  useEffect(() => {
    const handlePaste = (event: ClipboardEvent) => {
      const item = Array.from(event.clipboardData?.items ?? []).find((entry) => entry.type.startsWith('image/'));
      const file = item?.getAsFile();
      if (file) {
        void loadBackgroundFile(file);
      }
    };
    window.addEventListener('paste', handlePaste);
    return () => window.removeEventListener('paste', handlePaste);
  }, []);

  const commit = (next: ShotMapState, message?: string) => {
    setPast((items) => [...items.slice(-49), cloneState(map)]);
    setFuture([]);
    setMap(next);
    if (message) showStatus(message);
  };

  const updateCharacter = (id: string, patch: Partial<CharacterMark>, message?: string) => {
    commit(
      {
        ...map,
        characters: map.characters.map((character) => (character.id === id ? { ...character, ...patch } : character))
      },
      message
    );
  };

  const rememberRecentCharacterColor = (color: string) => {
    const normalized = normalizeHexColor(color);
    if (!normalized) return;
    setRecentCharacterColors((items) => [normalized, ...items.filter((item) => item !== normalized)].slice(0, MAX_RECENT_CHARACTER_COLORS));
  };

  const updateSelectedCharacterColor = (color: string) => {
    if (!selectedCharacter) return;
    updateCharacter(selectedCharacter.id, { color });
  };

  const updateCamera = (id: string, patch: Partial<CameraMark>, message?: string) => {
    commit(
      {
        ...map,
        cameras: map.cameras.map((camera) => (camera.id === id ? { ...camera, ...patch } : camera))
      },
      message
    );
  };

  const undo = () => {
    if (!past.length) return;
    const previous = past[past.length - 1];
    setPast((items) => items.slice(0, -1));
    setFuture((items) => [cloneState(map), ...items.slice(0, 49)]);
    setMap(previous);
    showStatus('已撤销一步。');
  };

  const redo = () => {
    if (!future.length) return;
    const next = future[0];
    setFuture((items) => items.slice(1));
    setPast((items) => [...items.slice(-49), cloneState(map)]);
    setMap(next);
    showStatus('已重做一步。');
  };

  const loadBackgroundFile = async (file: File) => {
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result !== 'string') return;
      commit(
        {
          ...map,
          background: reader.result,
          backgroundName: file.name,
          shotTitle: '自定义站位图',
          sceneIntent: '用户上传底图，请以画面中的角色位置、朝向、移动路径和机位标记作为场面调度依据。'
        },
        `已载入底图：${file.name}`
      );
    };
    reader.readAsDataURL(file);
  };

  const handleStageClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (!stageRef.current || dragging?.moved) return;
    const target = event.target instanceof HTMLElement ? event.target : null;
    if (target?.closest('.character-pin, .camera-pin')) return;
    const point = pointFromEvent(event, stageRef.current);
    if (tool === 'character') {
      const name = draftName.trim() || `角色${map.characters.length + 1}`;
      const character: CharacterMark = {
        id: makeId('char'),
        name,
        color: draftColor,
        labelSize: draftLabelSize,
        labelWeight: draftLabelWeight,
        x: point.x,
        y: point.y,
        facing: undefined,
        path: []
      };
      commit(
        {
          ...map,
          characters: [...map.characters, character]
        },
        `已添加角色：${name}。已切回选择模式，可直接编辑当前角色。`
      );
      setSelection({ kind: 'character', id: character.id });
      setTool('select');
      setDraftName(`角色${map.characters.length + 2}`);
      return;
    }

    if (tool === 'camera') {
      const camera: CameraMark = {
        id: makeId('cam'),
        label: `机位${map.cameras.length + 1}`,
        x: point.x,
        y: point.y,
        angle: 270,
        shotType: '中景',
        movement: '固定'
      };
      commit(
        {
          ...map,
          cameras: [...map.cameras, camera]
        },
        `已添加机位：${camera.label}`
      );
      setSelection({ kind: 'camera', id: camera.id });
      return;
    }

    if (tool === 'facing' && selectedCharacter) {
      updateCharacter(selectedCharacter.id, { facing: angleBetween(selectedCharacter, point) }, `已设置 ${selectedCharacter.name} 的朝向。`);
      return;
    }

    if (tool === 'path' && selectedCharacter) {
      updateCharacter(
        selectedCharacter.id,
        { path: [...selectedCharacter.path, point] },
        `已为 ${selectedCharacter.name} 添加移动节点。`
      );
      return;
    }

    if (tool === 'path' || tool === 'facing') {
      showStatus('请先选中一个角色，再设置朝向或移动路径。');
      return;
    }

    setSelection(null);
  };

  const startDrag = (event: React.PointerEvent, kind: 'character' | 'camera', id: string) => {
    event.stopPropagation();
    setSelection({ kind, id } as Selection);
    if (tool !== 'select') return;
    dragSnapshotRef.current = cloneState(map);
    setDragging({ kind, id, moved: false });
  };

  const handlePointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!dragging || !stageRef.current) return;
    const point = pointFromEvent(event, stageRef.current);
    setDragging((current) => (current ? { ...current, moved: true } : current));
    setMap((current) => {
      if (dragging.kind === 'character') {
        return {
          ...current,
          characters: current.characters.map((character) =>
            character.id === dragging.id ? { ...character, x: point.x, y: point.y } : character
          )
        };
      }
      return {
        ...current,
        cameras: current.cameras.map((camera) => (camera.id === dragging.id ? { ...camera, x: point.x, y: point.y } : camera))
      };
    });
  };

  const finishDrag = () => {
    if (dragging?.moved && dragSnapshotRef.current) {
      setPast((items) => [...items.slice(-49), dragSnapshotRef.current as ShotMapState]);
      setFuture([]);
      showStatus('已移动对象。');
    }
    dragSnapshotRef.current = null;
    setDragging(null);
  };

  const deleteSelected = () => {
    if (!selection) return;
    if (selection.kind === 'character') {
      commit(
        {
          ...map,
          characters: map.characters.filter((character) => character.id !== selection.id)
        },
        '已删除角色。'
      );
    } else {
      commit(
        {
          ...map,
          cameras: map.cameras.filter((camera) => camera.id !== selection.id)
        },
        '已删除机位。'
      );
    }
    setSelection(null);
  };

  const clearSelectedPath = () => {
    if (!selectedCharacter) return;
    updateCharacter(selectedCharacter.id, { path: [] }, `已清除 ${selectedCharacter.name} 的移动路径。`);
  };

  const moveSelectedBy = (deltaX: number, deltaY: number) => {
    if (selectedCharacter) {
      updateCharacter(
        selectedCharacter.id,
        {
          x: clamp(selectedCharacter.x + deltaX, 0, 100),
          y: clamp(selectedCharacter.y + deltaY, 0, 100)
        },
        `已微调 ${selectedCharacter.name} 的位置。`
      );
      return;
    }
    if (selectedCamera) {
      updateCamera(
        selectedCamera.id,
        {
          x: clamp(selectedCamera.x + deltaX, 0, 100),
          y: clamp(selectedCamera.y + deltaY, 0, 100)
        },
        `已微调 ${selectedCamera.label} 的位置。`
      );
      return;
    }
    showStatus('请先选中角色或机位，再使用方向键微调。');
  };

  const copyText = async (text: string, message: string) => {
    await navigator.clipboard.writeText(text);
    showStatus(message);
  };

  const handleExport = async () => {
    showStatus('正在截取当前画面，并在右下角叠加图例。');
    try {
      await exportPng(map, stageRef.current);
      showStatus('已导出当前画面 PNG，右下角图例已自动叠加。');
    } catch (error) {
      console.error(error);
      showStatus('导出失败，请稍后重试或刷新页面后再试。');
    }
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    const file = Array.from(event.dataTransfer.files).find((entry) => entry.type.startsWith('image/'));
    if (file) void loadBackgroundFile(file);
  };

  const startShortcutRecording = (action: ShortcutAction) => {
    setRecordingAction(action);
    setShortcutConflict('');
  };

  const resetShortcutAction = (action: ShortcutAction) => {
    const defaults = createDefaultShortcutMap();
    setShortcuts((current) => ({
      ...current,
      [action]: [...defaults[action]]
    }));
    setRecordingAction(null);
    setShortcutConflict('');
    showStatus('已恢复该快捷键默认值。');
  };

  const resetAllShortcuts = () => {
    setShortcuts(createDefaultShortcutMap());
    setRecordingAction(null);
    setShortcutConflict('');
    showStatus('已恢复全部快捷键默认值。');
  };

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (shortcutSettingsOpen) {
        if (recordingAction) {
          event.preventDefault();
          if (event.key === 'Escape') {
            setRecordingAction(null);
            setShortcutConflict('');
            return;
          }
          const combo = eventToShortcutCombo(event);
          if (!combo) return;
          const conflict = findShortcutConflict(shortcuts, recordingAction, combo);
          if (conflict) {
            setShortcutConflict(`“${formatShortcutCombo(combo)}” 已被“${conflict.label}”使用，请换一个快捷键。`);
            return;
          }
          setShortcuts((current) => ({
            ...current,
            [recordingAction]: [combo]
          }));
          setRecordingAction(null);
          setShortcutConflict('');
          showStatus(`已设置快捷键：${formatShortcutCombo(combo)}。`);
          return;
        }
        if (event.key === 'Escape') {
          event.preventDefault();
          setShortcutSettingsOpen(false);
          setShortcutConflict('');
        }
        return;
      }

      if (isEditableShortcutTarget(event.target) || dragging) return;

      if (event.key.startsWith('Arrow')) {
        event.preventDefault();
        const step = event.altKey ? 0.08 : event.shiftKey ? 1.2 : 0.35;
        const delta = {
          ArrowUp: { x: 0, y: -step },
          ArrowDown: { x: 0, y: step },
          ArrowLeft: { x: -step, y: 0 },
          ArrowRight: { x: step, y: 0 }
        }[event.key];
        if (delta) moveSelectedBy(delta.x, delta.y);
        return;
      }

      const action = shortcutActionForEvent(event, shortcuts);
      if (!action) return;
      event.preventDefault();

      if (action === 'tool.select') {
        setTool('select');
        setFocusDraftName(false);
        showStatus('已切换到选择工具。');
        return;
      }
      if (action === 'tool.character') {
        setTool('character');
        setSelection(null);
        setFocusDraftName(false);
        showStatus('已切换到添加角色。输入名称、选择颜色后点击画面落点。');
        return;
      }
      if (action === 'tool.facing') {
        setTool('facing');
        setFocusDraftName(false);
        showStatus(selectedCharacter ? `点击画面设置 ${selectedCharacter.name} 的朝向。` : '已切换到朝向工具，请先选中角色。');
        return;
      }
      if (action === 'tool.path') {
        setTool('path');
        setFocusDraftName(false);
        showStatus(selectedCharacter ? `点击画面为 ${selectedCharacter.name} 添加移动节点。` : '已切换到路径工具，请先选中角色。');
        return;
      }
      if (action === 'tool.camera') {
        setTool('camera');
        setFocusDraftName(false);
        showStatus('已切换到添加机位。点击画面放置新机位。');
        return;
      }
      if (action === 'file.load') {
        fileRef.current?.click();
        return;
      }
      if (action === 'edit.undo') {
        undo();
        return;
      }
      if (action === 'edit.redo') {
        redo();
        return;
      }
      if (action === 'edit.deleteSelected') {
        if (selection) deleteSelected();
        else showStatus('请先选中角色或机位，再删除。');
        return;
      }
      if (action === 'edit.clearPath') {
        if (selectedCharacter) clearSelectedPath();
        else showStatus('请先选中角色，再清除路径。');
        return;
      }
      if (action === 'export.png') {
        void handleExport();
        return;
      }
      if (action === 'copy.prompt') {
        void copyText(prompt, '已复制 Prompt。');
        return;
      }
      if (action === 'copy.json') {
        void copyText(JSON.stringify(map, null, 2), '已复制 JSON。');
        return;
      }
      if (action === 'view.cancel') {
        setSelection(null);
        setTool('select');
        setFocusDraftName(false);
        showStatus('已取消选择并切回选择工具。');
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [
    dragging,
    future,
    map,
    past,
    prompt,
    recordingAction,
    selectedCamera,
    selectedCharacter,
    selection,
    shortcutSettingsOpen,
    shortcuts,
    tool
  ]);

  const selectedName = selectedCharacter?.name ?? selectedCamera?.label ?? '未选择对象';
  const isSupportPage = window.location.pathname.replace(/\/$/, '') === '/support';
  const shortcutLabel = (action: ShortcutAction) => formatShortcutCombo(shortcuts[action]?.[0] ?? '未设');

  if (isSupportPage) {
    return <SupportPage />;
  }

  return (
    <main className="app-shell" onDrop={handleDrop} onDragOver={(event) => event.preventDefault()}>
      <header className="topbar">
        <div className="brand-block">
          <div className="brand-kicker">AI video blocking tool</div>
          <h1>ShotMap Studio</h1>
        </div>
        <nav className="tool-strip" aria-label="编辑工具">
          <button className="toolbar-button" onClick={() => fileRef.current?.click()} title="载入底图">
            <Upload size={18} />
            <span>载入</span>
            <ShortcutKey>{shortcutLabel('file.load')}</ShortcutKey>
          </button>
          <button className={tool === 'select' ? 'toolbar-button active' : 'toolbar-button'} onClick={() => setTool('select')} title="选择和移动">
            <MousePointer2 size={18} />
            <span>选择</span>
            <ShortcutKey>{shortcutLabel('tool.select')}</ShortcutKey>
          </button>
          <button
            className={tool === 'character' ? 'toolbar-button active' : 'toolbar-button'}
            onClick={() => {
              setTool('character');
              setSelection(null);
              setFocusDraftName(true);
              showStatus('输入名称、选择颜色，然后点击画面放置新角色。');
            }}
            title="添加角色"
          >
            <LocateFixed size={18} />
            <span>角色</span>
            <ShortcutKey>{shortcutLabel('tool.character')}</ShortcutKey>
          </button>
          <button className={tool === 'facing' ? 'toolbar-button active' : 'toolbar-button'} onClick={() => setTool('facing')} title="设置面朝方向">
            <PenLine size={18} />
            <span>朝向</span>
            <ShortcutKey>{shortcutLabel('tool.facing')}</ShortcutKey>
          </button>
          <button className={tool === 'path' ? 'toolbar-button active' : 'toolbar-button'} onClick={() => setTool('path')} title="添加移动轨迹">
            <Route size={18} />
            <span>路径</span>
            <ShortcutKey>{shortcutLabel('tool.path')}</ShortcutKey>
          </button>
          <button className={tool === 'camera' ? 'toolbar-button active' : 'toolbar-button'} onClick={() => setTool('camera')} title="添加机位">
            <Camera size={18} />
            <span>机位</span>
            <ShortcutKey>{shortcutLabel('tool.camera')}</ShortcutKey>
          </button>
          <button className="icon-button" disabled={!past.length} onClick={undo} title="撤销：Cmd/Ctrl + Z">
            <Undo2 size={18} />
          </button>
          <button className="icon-button" disabled={!future.length} onClick={redo} title="重做：Cmd/Ctrl + Shift + Z">
            <Redo2 size={18} />
          </button>
          <button className="toolbar-button primary" onClick={() => void handleExport()} title="导出 PNG">
            <Download size={18} />
            <span>PNG</span>
            <ShortcutKey>{shortcutLabel('export.png')}</ShortcutKey>
          </button>
          <button className="toolbar-button" onClick={() => setShortcutSettingsOpen(true)} title="快捷键设置">
            <Keyboard size={18} />
            <span>快捷键</span>
          </button>
          <a className="toolbar-button" href="/support" title="支持作者">
            <HeartHandshake size={18} />
            <span>支持作者</span>
          </a>
        </nav>
        <input
          ref={fileRef}
          type="file"
          accept="image/png,image/jpeg,image/webp"
          className="hidden-input"
          onChange={(event) => {
            const file = event.target.files?.[0];
            if (file) void loadBackgroundFile(file);
            event.currentTarget.value = '';
          }}
        />
      </header>
      <div className="toast-viewport" aria-live="polite" aria-atomic="true">
        {toast && (
          <div key={toast.id} className="status-toast">
            <CheckCircle2 size={18} aria-hidden="true" />
            <span>{toast.message}</span>
          </div>
        )}
      </div>
      {shortcutSettingsOpen && (
        <ShortcutSettingsDialog
          shortcuts={shortcuts}
          recordingAction={recordingAction}
          conflictMessage={shortcutConflict}
          onClose={() => {
            setShortcutSettingsOpen(false);
            setRecordingAction(null);
            setShortcutConflict('');
          }}
          onStartRecording={startShortcutRecording}
          onResetAction={resetShortcutAction}
          onResetAll={resetAllShortcuts}
        />
      )}

      <section className="workspace">
        <aside className="left-rail">
          <div className="panel-title">
            <FolderOpen size={16} />
            <span>镜头</span>
          </div>
          <button className="shot-card active">
            <span className="shot-number">01</span>
            <span>
              <strong>{map.shotTitle || SAMPLE_SHOT_TITLE}</strong>
              <small>{map.characters.length} 角色 · {map.cameras.length} 机位</small>
            </span>
          </button>
          <div className="mini-section">
            <span className="section-label">示例意图</span>
            <p>{map.sceneIntent || SAMPLE_SHOT_INTENT}</p>
          </div>
          <div className="mini-section">
            <span className="section-label">图例说明</span>
            <div className="legend-list" aria-label="站位图图例">
              <div className="legend-row">
                <span className="legend-dot" />
                <span>圆点表示角色当前位置。</span>
              </div>
              <div className="legend-row">
                <span className="legend-facing" />
                <span>角色圆点长出的尖角表示面朝方向。</span>
              </div>
              <div className="legend-row">
                <span className="legend-arrow dashed" />
                <span>虚线箭头表示角色移动轨迹和移动方向。</span>
              </div>
              <div className="legend-row">
                <span className="legend-camera" />
                <span>琥珀色机位表示镜头位置和拍摄方向。</span>
              </div>
            </div>
          </div>
          <div className="mini-section">
            <span className="section-label">模板入口</span>
            <button
              className="ghost-action"
              onClick={() => {
                commit(initialMap, `已恢复“${SAMPLE_SHOT_TITLE}”示例模板。`);
                setSelection(null);
              }}
            >
              载入雨巷会合模板
            </button>
          </div>
          <div className="mini-section">
            <span className="section-label">操作提示</span>
            <p>选择“角色”后点击画面落点。选中角色后切到“朝向”或“路径”，再点击画面设置方向与轨迹。</p>
          </div>
          <ShortcutHelpPanel groups={visibleShortcutGroups} onOpenSettings={() => setShortcutSettingsOpen(true)} />
        </aside>

        <section className="stage-column">
          <div className="stage-header">
            <div>
              <span className="eyebrow">当前底图</span>
              <strong>{map.backgroundName}</strong>
            </div>
            <span className="status-line">{status}</span>
          </div>

          <div
            ref={stageRef}
            className={`stage tool-${tool}`}
            onClick={handleStageClick}
            onPointerMove={handlePointerMove}
            onPointerUp={finishDrag}
            onPointerLeave={finishDrag}
          >
            <img className="stage-image" src={map.background} alt="场景底图" draggable={false} />
            <svg className="annotation-layer" viewBox="0 0 100 100" preserveAspectRatio="none" aria-label="站位标注层">
              {map.characters.map((character) => {
                const points = [character, ...character.path];
                const pathData = points.map((point, index) => `${index === 0 ? 'M' : 'L'} ${point.x} ${point.y}`).join(' ');
                const arrowFrom = points[points.length - 2];
                const arrowTo = points[points.length - 1];
                return character.path.length ? (
                  <g key={`path-${character.id}`}>
                    <path
                      className="movement-path"
                      d={pathData}
                      fill="none"
                      stroke={character.color}
                      strokeWidth={0.62}
                      strokeDasharray="2.2 1.45"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path className="arrow-tip" d={arrowTipPath(arrowFrom, arrowTo, 1.88, 1)} style={{ fill: character.color, stroke: character.color }} />
                  </g>
                ) : null;
              })}
              {map.cameras.map((camera) => {
                const end = vectorPoint(camera, camera.angle, 4.9);
                return (
                  <g key={camera.id} className="camera-vector">
                    <path className="camera-tip" d={arrowTipPath(camera, end, 1.85, 0.92)} style={{ fill: '#DDAA33', stroke: '#DDAA33' }} />
                  </g>
                );
              })}
            </svg>

            {map.cameras.map((camera) => (
              <button
                key={camera.id}
                className={selection?.kind === 'camera' && selection.id === camera.id ? 'camera-pin selected' : 'camera-pin'}
                style={{ left: `${camera.x}%`, top: `${camera.y}%` }}
                onPointerDown={(event) => startDrag(event, 'camera', camera.id)}
                onClick={(event) => event.stopPropagation()}
                title={camera.label}
              >
                <Aperture size={18} />
                <span>{camera.label}</span>
              </button>
            ))}

            {map.characters.map((character, index) => {
              const isSelected = selection?.kind === 'character' && selection.id === character.id;
              const placement = labelPlacementForCharacter(character, map);
              return (
                <button
                  key={character.id}
                  className={[
                    'character-pin',
                    character.facing !== undefined ? 'has-facing' : '',
                    isSelected ? 'selected' : '',
                    `label-${placement}`
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  style={
                    {
                      left: `${character.x}%`,
                      top: `${character.y}%`,
                      '--pin-color': character.color,
                      '--label-font-size': `${normalizeLabelSize(character.labelSize)}px`,
                      '--label-font-weight': normalizeLabelWeight(character.labelWeight),
                      '--facing-angle': `${normalizeAngle(character.facing ?? 0)}deg`
                    } as React.CSSProperties
                  }
                  onPointerDown={(event) => startDrag(event, 'character', character.id)}
                  onClick={(event) => event.stopPropagation()}
                  title={character.name}
                >
                  <span className="pin-label">{character.name}</span>
                  <span className="pin-dot">{index + 1}</span>
                </button>
              );
            })}
          </div>
        </section>

        <aside className="right-rail">
          {tool === 'character' && (
            <div className="panel-block">
              <div className="panel-title">
                <BringToFront size={16} />
                <span>待新增角色</span>
              </div>
              <label className="field-label">
                名称
                <input value={draftName} onChange={(event) => setDraftName(event.target.value)} placeholder="例如：女主" autoFocus={focusDraftName} />
              </label>
              <div className="field-help">创建后，这个名称会显示在角色圆点上。</div>
              <div className="field-label">
                颜色
                <CharacterColorPicker
                  value={draftColor}
                  recentColors={recentCharacterColors}
                  onChange={setDraftColor}
                  onCustomColor={rememberRecentCharacterColor}
                />
              </div>
              <div className="field-label">
                名称排版
                <CharacterLabelTypographyControls
                  size={draftLabelSize}
                  weight={draftLabelWeight}
                  onSizeChange={setDraftLabelSize}
                  onWeightChange={setDraftLabelWeight}
                />
              </div>
            </div>
          )}

          <div className="panel-block">
            <div className="panel-title">
              <LocateFixed size={16} />
              <span>属性</span>
            </div>
            <div className="selected-name">{selectedName}</div>
            {selectedCharacter && (
              <div className="inspector-stack">
                <label className="field-label">
                  当前角色名称
                  <input value={selectedCharacter.name} onChange={(event) => updateCharacter(selectedCharacter.id, { name: event.target.value })} />
                </label>
                <div className="field-label">
                  角色颜色
                  <CharacterColorPicker
                    value={selectedCharacter.color}
                    recentColors={recentCharacterColors}
                    onChange={updateSelectedCharacterColor}
                  onCustomColor={rememberRecentCharacterColor}
                />
              </div>
              <div className="field-label">
                名称排版
                <CharacterLabelTypographyControls
                  size={normalizeLabelSize(selectedCharacter.labelSize)}
                  weight={normalizeLabelWeight(selectedCharacter.labelWeight)}
                  onSizeChange={(labelSize) => updateCharacter(selectedCharacter.id, { labelSize })}
                  onWeightChange={(labelWeight) => updateCharacter(selectedCharacter.id, { labelWeight })}
                />
              </div>
              <label className="field-label">
                  朝向角度
                  <input
                    type="range"
                    min="0"
                    max="359"
                    value={Math.round(selectedCharacter.facing ?? 0)}
                    onChange={(event) => updateCharacter(selectedCharacter.id, { facing: Number(event.target.value) })}
                  />
                </label>
                <div className="two-actions">
                  <button className="ghost-action" onClick={clearSelectedPath}>清除路径</button>
                  <button className="danger-action" onClick={deleteSelected}>
                    <Trash2 size={15} />
                    删除
                  </button>
                </div>
              </div>
            )}
            {selectedCamera && (
              <div className="inspector-stack">
                <label className="field-label">
                  机位名
                  <input value={selectedCamera.label} onChange={(event) => updateCamera(selectedCamera.id, { label: event.target.value })} />
                </label>
                <label className="field-label">
                  镜头类型
                  <input value={selectedCamera.shotType} onChange={(event) => updateCamera(selectedCamera.id, { shotType: event.target.value })} />
                </label>
                <label className="field-label">
                  镜头运动
                  <input value={selectedCamera.movement} onChange={(event) => updateCamera(selectedCamera.id, { movement: event.target.value })} />
                </label>
                <label className="field-label">
                  朝向角度
                  <input
                    type="range"
                    min="0"
                    max="359"
                    value={Math.round(selectedCamera.angle)}
                    onChange={(event) => updateCamera(selectedCamera.id, { angle: Number(event.target.value) })}
                  />
                </label>
                <button className="danger-action" onClick={deleteSelected}>
                  <Trash2 size={15} />
                  删除
                </button>
              </div>
            )}
            {!selection && <p className="empty-copy">点击角色或机位后，可在这里修改属性。</p>}
          </div>

          <div className="panel-block export-panel">
            <div className="panel-title">
              <Clipboard size={16} />
              <span>导出</span>
            </div>
            <textarea readOnly value={prompt} />
            <div className="two-actions">
              <button className="ghost-action" onClick={() => void copyText(prompt, '已复制 Prompt。')}>
                <Clipboard size={15} />
                Prompt
              </button>
              <button className="ghost-action" onClick={() => void copyText(JSON.stringify(map, null, 2), '已复制 JSON。')}>
                <FileJson size={15} />
                JSON
              </button>
            </div>
          </div>
        </aside>
      </section>
    </main>
  );
}

export default App;
